import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
  Fragment,
  useRef,
  MouseEvent,
} from 'react';

import ProductSummary, {
  ProductSummarySkeleton,
} from '@/components/ProductSummary';
import {
  IntersectionObserverContext,
  createIntersectionObserver,
  useObserver,
} from '@/hooks/useObserver';
import useRequest from '@/hooks/useRequest';
import { fetchCategory } from '@/lib/categories';
import { Props } from './ProductGrid.d';
import { Flex, Button, SimpleGrid, Box, SimpleGridProps } from '@thebouqs/ui';
import isBrowser from '@/utils/isBrowser';

const simpleGridProps: SimpleGridProps = {
  className: 'product-grid product-grid--show-subscriber',
  gridColumnGap: ['5', '7'],
  spacingY: '8',
  width: '100%',
  mx: 'auto',
  mt: 'md',
  boxSizing: 'border-box',
  columns: [2, null, null, 3],
};
const ProductGrid = ({
  products,
  handleCatalogPage,
  lastProductId,
  catalog,
  lazyLoadPgNumber,
  setLazyLoadPgNumber,
  setNetworkStatus,
  isLazyLoading,
  setNavStrategy,
  isLastPage,
  setLastPage,
  infiniteScroll,
  cmsSlug,
  //sort and filters come from [category_slug] are are needed in ProductSummary for click event
  sort,
  activeFilters,
}: Props): JSX.Element => {
  const gridRef = useRef();
  const [pageInstance, setPageInstance] = useState(null);
  const [blockLazyLoad, setBlockLazyLoad] = useState(!infiniteScroll);
  const pageObserver = useObserver(pageInstance);

  // Set the request qs to lazyLoadPgNumber, this way it appends consecutive page numbers
  let searchParams = null;
  let qs = null;

  if (isBrowser()) {
    const search = window.location.search;
    searchParams = new URLSearchParams(search);
    searchParams.set('page', lazyLoadPgNumber.current);
    // need qs to be updated with correct page number while lazy loading
    qs = searchParams.toString() ?? '';
  }

  const fetcherWithOptions = () =>
    fetchCategory(catalog.category_type, cmsSlug ? cmsSlug : catalog.slug, qs);

  const { data, isLoading, error } = useRequest(
    catalog.category_type,
    cmsSlug ? cmsSlug : catalog.slug,
    qs,
    fetcherWithOptions,
    { initialData: catalog }
  );

  // So ts lets us use page count and not have to cast it using as
  let pageCount = null;
  if (data && 'page_count' in data) {
    pageCount = data.page_count;
  }

  error && setNetworkStatus('ERROR');

  const lazyFetchPage = () => {
    // Remove lazyLoad button when user scrolled up and down again
    if (blockLazyLoad) {
      setBlockLazyLoad(false);
    }
    setNavStrategy('LAZY');
    // Update page number so component re-renders and fetches new page
    setLazyLoadPgNumber(lazyLoadPgNumber.current + 1);
  };

  const unblockLazyLoad = (e: MouseEvent<HTMLButtonElement>) => {
    const position = e.currentTarget.getBoundingClientRect();
    const newY = window.scrollY - window.innerHeight + position.top;
    lazyFetchPage();
    window.requestAnimationFrame(() => {
      window.scrollTo(0, newY);
    });
  };

  // Triggers only when lazy loading
  const observed = useCallback(node => {
    if (node && !pageInstance) {
      setPageInstance(node);
      node.addEventListener('intersects', () => {
        // Append only if we are scrolling and not using pagination
        // Get the scrollY of the element and get the currentScroll
        const isViewportBottom =
          window.innerHeight + window.scrollY >= document.body.offsetHeight;

        if (isViewportBottom) {
          setBlockLazyLoad(true);
          return;
        }

        lazyFetchPage();
        pageObserver?.unobserve(node);
      });
    }
  }, []);

  const isNotMaxPage = lazyLoadPgNumber.current <= pageCount;

  // Append products
  useEffect(() => {
    if (data && isLazyLoading && lazyLoadPgNumber.current > 1 && isNotMaxPage) {
      appendProducts();
    }
  }, [lazyLoadPgNumber.current, data, isLazyLoading]);

  // Handles fetch every time boundary intersects
  const appendProducts = () => {
    // Check if we have products before we access it
    let newCatalogProducts = null;

    if (data && 'products' in data) {
      newCatalogProducts = data.products;

      if (newCatalogProducts.length === 0) {
        setLastPage(true);
      }
    }

    const isNotTheSameCatalog =
      catalog.products[0]?.id !== newCatalogProducts[0]?.id;

    // If catalog fetched is not empty then append products
    if (
      isNotTheSameCatalog &&
      newCatalogProducts &&
      newCatalogProducts.length > 0
    ) {
      const allProducts = [...catalog.products, ...newCatalogProducts];
      const catalogWithAllProducts = { ...catalog, products: allProducts };

      // Grab last productId from the list of all catalog products
      lastProductId =
        catalogWithAllProducts.products[
          catalogWithAllProducts.products.length - 1
        ].id;

      handleCatalogPage(catalogWithAllProducts);
    }
  };

  const observer: IntersectionObserver = useMemo(() => {
    if (gridRef.current) {
      return createIntersectionObserver({
        margin: '50% 0px 0px 0px',
        root: null,
      });
    }
  }, [products, gridRef.current]);

  // trigger lazy load if we have more than one page and we haven't exceeded max page.
  const triggerLazyLoad = () => {
    if (lazyLoadPgNumber.current > pageCount) return false;
    if (pageCount > 1) return true;

    return false;
  };

  return (
    <>
      <SimpleGrid {...simpleGridProps} ref={gridRef} data-testid="product-grid">
        <IntersectionObserverContext.Provider value={{ observer }}>
          {products.map((product, index) => (
            <Fragment key={product.id}>
              <Box
                width="auto"
                maxWidth="100%"
                className="product-grid__cell"
                data-testid="product-grid__cell">
                <ProductSummary
                  sort={sort}
                  activeFilters={activeFilters}
                  categoryPath={catalog?.path}
                  product={product}
                  productIndex={index}
                />
              </Box>
              {product.id === lastProductId &&
                lazyLoadPgNumber.current &&
                triggerLazyLoad() && <div ref={observed} />}
            </Fragment>
          ))}
        </IntersectionObserverContext.Provider>
        {isLoading && (
          <ProductGridSkeleton data-testid="product-grid-skeleton" count={3} />
        )}
      </SimpleGrid>
      <Flex justifyContent="center" my="md" height="2.5rem">
        {blockLazyLoad && !isLastPage ? (
          <Button
            colorScheme="green"
            variant="outline"
            height="100%"
            onClick={unblockLazyLoad}>
            Load more Bouqs
          </Button>
        ) : null}
      </Flex>
    </>
  );
};

export const ProductGridSkeleton = ({
  count = 9,
  requiresOuterGrid = false,
}: {
  count?: number;
  requiresOuterGrid?: boolean;
}): JSX.Element => {
  const gridInnerContent = Array(count)
    .fill(1)
    .map((_, index) => (
      <Box
        width="auto"
        maxWidth="100%"
        key={index}
        className="product-grid-skeleton__cell"
        data-testid="product-grid-skeleton__cell">
        <ProductSummarySkeleton />
      </Box>
    ));
  return requiresOuterGrid ? (
    <SimpleGrid {...simpleGridProps}>{gridInnerContent}</SimpleGrid>
  ) : (
    <>{gridInnerContent}</>
  );
};

export default ProductGrid;
